import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import { useCallback } from 'react';
import { Spinner } from '@repo/components';
import { PERMISSIONS, InviteData, LinkData, User } from '@repo/shared/domain';
import { EventEmitter, getVerifyPermission, guardedClient as request } from '@repo/shared/helpers';
import UsersTab from 'src/parts/tabs/UsersTab';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import { getEvent, getEventUsers, getEventRoles } from 'src/api/admin/events';

interface UsersSearch {
	page?: number;
	sort?: string;
	filter?: string;
}

export const Route = createFileRoute('/_admin/events/$eventId/users')({
	validateSearch: (search: Record<string, unknown>): UsersSearch => ({
		page: search.page as number | undefined,
		sort: search.sort as string | undefined,
		filter: search.filter as string | undefined,
	}),
	component: UsersPage,
});

function UsersPage() {
	const { eventId } = Route.useParams();
	const { page, sort, filter } = Route.useSearch();
	const queryClient = useQueryClient();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const { config, loading: configLoading } = useConfig();
	const defaultCountry = config?.defaultCountry || 'BE';

	const { data: event, isLoading: eventLoading } = useQuery({
		queryKey: ['event', eventId],
		queryFn: () => getEvent(eventId),
	});

	const { data: usersData, isLoading: usersLoading } = useQuery({
		queryKey: ['event-users', eventId, page, sort, filter],
		queryFn: () => getEventUsers(eventId, { page, sort, filter }),
		enabled: !!event,
	});

	const { data: roles } = useQuery({
		queryKey: ['event-roles', eventId],
		queryFn: () => getEventRoles(eventId),
		enabled: !!event,
	});

	const verifyPermission = authenticatedUser ? getVerifyPermission(authenticatedUser.permissions) : () => false;

	const revalidateEventDetail = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['event', eventId] });
		queryClient.invalidateQueries({ queryKey: ['event-users', eventId] });
	}, [queryClient, eventId]);

	const handleLinkUser = useCallback(
		async (linkData: LinkData) => {
			const { userId, roleIds } = linkData;

			if (!userId || event?.userIds?.includes(userId)) {
				return;
			}

			try {
				await request.post(`/api/events/${eventId}/users`, { userId, roleIds });
				EventEmitter.emit('toast', { type: 'success', message: 'Changes saved' });
			} catch (error) {
				console.error(error);
				EventEmitter.emit('toast', { type: 'error', message: 'Error linking user to event' });
			}
			revalidateEventDetail();
		},
		[eventId, revalidateEventDetail, event?.userIds],
	);

	const handleInviteUser = useCallback(
		async (data: InviteData) => {
			try {
				const { body: user } = await request.post<User>('/api/invites/users', data);
				await handleLinkUser({ userId: user.id });
				return user;
			} catch (error: unknown) {
				const err = error as { message?: string };
				EventEmitter.emit('toast', {
					type: 'error',
					message: err?.message || 'Error adding user to event',
				});
				throw error;
			}
		},
		[handleLinkUser],
	);

	const handleDeleteUser = useCallback(
		async (userId: string) => {
			try {
				await request.del(`/api/events/${eventId}/users/${userId}`, {});
				EventEmitter.emit('toast', { type: 'success', message: 'Changes saved' });
			} catch (_error) {
				EventEmitter.emit('toast', { type: 'error', message: 'Error' });
			}
			revalidateEventDetail();
		},
		[eventId, revalidateEventDetail],
	);

	const handleReload = () => {
		revalidateEventDetail();
	};

	const isLoading = authLoading || configLoading || eventLoading || usersLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	if (!event || !authenticatedUser) return null;

	// Show alert if inheritUsersFromGroups is true
	if (event?.type?.inheritUsersFromGroups) {
		return (
			<div className="p-6">
				<Alert severity="info" variant="filled">
					<strong>All Company Users Have Access</strong>
					<br />
					This event type is configured to inherit users from all company groups. All users belonging to the assigned
					companies will automatically have access to this event.
				</Alert>
			</div>
		);
	}

	return (
		<UsersTab
			onInvite={handleInviteUser}
			onLink={handleLinkUser}
			onDelete={handleDeleteUser}
			onReload={handleReload}
			event={event}
			roles={roles || []}
			authenticatedUser={authenticatedUser}
			users={usersData || { data: [], count: 0, page: 0, pageSize: 25 }}
			readOnly={false}
			filter={filter}
			permissions={{
				archive: verifyPermission(PERMISSIONS.adminUsersArchive),
				update: verifyPermission(PERMISSIONS.adminUsersEdit),
				create: verifyPermission(PERMISSIONS.adminUsersCreate),
				canPrintBadges: verifyPermission(PERMISSIONS.adminSecurityBadgePrinting),
				canCreateWithoutEmail: verifyPermission(PERMISSIONS.adminUsersCreate),
				isUserAdmin: verifyPermission(PERMISSIONS.adminUsersCreate),
				canImport: verifyPermission(PERMISSIONS.adminUsersImport),
			}}
			backUrlLocalStorageKey="usersBackUrl"
			addUserButtonState={{
				disabled: event.source === 'calendar',
				tooltip: 'This event does not allow adding users through this interface.',
			}}
			defaultCountry={defaultCountry}
		/>
	);
}
